/**
 * Org-level (app-wide) default date range for all RCM services.
 * Source of truth: SaaS organizations.settings.rcm
 *   default_date_range: '30' | '90' | '180' | 'custom'
 *   default_date_from / default_date_to: YYYY-MM-DD when custom
 */

export const ORG_DEFAULT_DATE_RANGE_OPTIONS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
  { value: 'custom', label: 'Custom date range' },
];

export function toIsoDate(d) {
  const x = d instanceof Date ? d : new Date(d);
  return x.toISOString().slice(0, 10);
}

/**
 * YYYY-MM-DD for Claims list/chart bucketing (DOS → submitted → created).
 */
export function claimListDateKey(claim) {
  const billable = claim?.billable_period;
  const bpStart = billable && typeof billable === 'object' ? billable.start : null;
  const candidates = [
    bpStart,
    claim?.submitted_at,
    claim?.submitted_date,
    claim?.date_of_service,
    claim?.created_at,
    claim?.created,
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    if (typeof raw === 'string' && raw.length >= 10 && raw[4] === '-' && raw[7] === '-') {
      return raw.slice(0, 10);
    }
    const d = raw instanceof Date ? raw : new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return d.toISOString().slice(0, 10);
    }
  }
  return null;
}

/**
 * Resolve the date window sent to Claims list APIs.
 * Custom org ranges use the exact saved from/to — do not roll end forward to today.
 */
export function resolveClaimsQueryDateWindow(window) {
  return window;
}

export function normalizeOrgDateRange(raw) {
  const cleaned = String(raw ?? '30').trim();
  if (cleaned === '30' || cleaned === '90' || cleaned === '180' || cleaned === 'custom') return cleaned;
  const n = parseInt(cleaned, 10);
  if (n === 90 || n === 180) return String(n);
  return '30';
}

/**
 * Resolve concrete window from org rcm settings (or override).
 * @param {object|null} rcmSettings - organizations.settings.rcm or remittance settings cache
 * @param {object|null} override - { range, date_from?, date_to? }
 */
export function resolveOrgDefaultDateWindow(rcmSettings, override = null) {
  const today = new Date();
  const todayIso = toIsoDate(today);

  const range = override?.range
    ? normalizeOrgDateRange(override.range)
    : normalizeOrgDateRange(rcmSettings?.default_date_range);

  if (range === 'custom') {
    let from = (override?.date_from || rcmSettings?.default_date_from || '').toString().slice(0, 10);
    let to = (override?.date_to || rcmSettings?.default_date_to || '').toString().slice(0, 10);
    if (!from || !to) {
      const start = new Date(today);
      start.setDate(today.getDate() - 29);
      return {
        range: '30',
        date_from: toIsoDate(start),
        date_to: todayIso,
        period_days: 30,
        label: 'Last 30 days',
        timeRangeToken: '30d',
        analyticsToken: 'last30days',
      };
    }
    if (from > to) {
      const tmp = from;
      from = to;
      to = tmp;
    }
    const period_days = Math.max(
      1,
      Math.round((new Date(`${to}T12:00:00`) - new Date(`${from}T12:00:00`)) / 86400000) + 1
    );
    return {
      range: 'custom',
      date_from: from,
      date_to: to,
      period_days,
      label: `${from} → ${to}`,
      timeRangeToken: 'custom',
      analyticsToken: { type: 'custom', start: from, end: to },
    };
  }

  const days = parseInt(range, 10) || 30;
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));
  return {
    range: String(days),
    date_from: toIsoDate(start),
    date_to: todayIso,
    period_days: days,
    label: `Last ${days} days`,
    timeRangeToken: `${days}d`,
    analyticsToken: days === 90 ? 'last90days' : days === 180 ? 'last180days' : 'last30days',
  };
}

/** Map org window → Claims/AR/Denial style '30d'|'90d'|'180d'|'custom'. */
export function toDashboardTimeRange(windowOrSettings) {
  const w =
    windowOrSettings?.timeRangeToken != null
      ? windowOrSettings
      : resolveOrgDefaultDateWindow(windowOrSettings);
  if (w.range === 'custom' || w.timeRangeToken === 'custom') return 'custom';
  if (w.period_days === 90) return '90d';
  if (w.period_days === 180) return '180d';
  return '30d';
}

/** Map org window → ChargeCapture / Denial list style 'last30days'|... */
export function toLastNDaysToken(windowOrSettings) {
  const w =
    windowOrSettings?.analyticsToken != null && typeof windowOrSettings.analyticsToken === 'string'
      ? windowOrSettings
      : resolveOrgDefaultDateWindow(windowOrSettings);
  if (typeof w.analyticsToken === 'string') return w.analyticsToken;
  return 'last30days';
}

/**
 * Map org window → RcmAnalyticsFilters dateRange value
 * ('last30days'|'last90days'|'last180days'|{type:'custom',start,end}).
 */
export function toAnalyticsDateRange(windowOrSettings) {
  const w =
    windowOrSettings?.analyticsToken != null
      ? windowOrSettings
      : resolveOrgDefaultDateWindow(windowOrSettings);
  if (w?.analyticsToken != null) return w.analyticsToken;
  return 'last30days';
}

/**
 * Resolve SaaS API root (.../api/v1) for org settings.
 * Micro-app iframes MUST NOT use their own origin (/api/v1 on :3154/:3155) — that 404s.
 * Prefer shell-passed saas_api_base (AgenticRCM ServiceFrame), then env, then localhost:8001.
 */
export function getSaasApiBaseUrlForOrgSettings() {
  if (typeof window === 'undefined' || !window.location) {
    return 'http://localhost:8001/api/v1';
  }

  const normalize = (raw) => {
    if (!raw || !String(raw).trim()) return null;
    let u = String(raw).trim().replace(/\/+$/, '');
    if (u.endsWith('/api/v1')) return u;
    if (u.endsWith('/api')) return `${u}/v1`;
    return `${u}/api/v1`;
  };

  try {
    const fromQuery = new URLSearchParams(window.location.search).get('saas_api_base');
    const q = normalize(fromQuery);
    if (q) return q;
  } catch {
    /* ignore */
  }

  const envRaw =
    process.env.REACT_APP_SAAS_API_URL ||
    process.env.REACT_APP_SAAS_FOUNDATION_API_URL ||
    '';
  const fromEnv = normalize(envRaw);
  if (fromEnv) return fromEnv;

  const { hostname, port, protocol } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  const isBareIp = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
  const isRealDomain = !isLocal && !isBareIp;

  // Real domain (per-customer HTTPS subdomains): the shell is one label up.
  // On the shell itself (customer.<apex>, 3 labels) → same-origin /api proxies to SaaS.
  // On a service subdomain (slug.customer.<apex>, 4+ labels) → target the shell by
  // stripping the leading label, so SaaS calls don't hit the service's own backend.
  if (isRealDomain) {
    const labels = hostname.split('.');
    if (labels.length >= 4) {
      const shellHost = labels.slice(1).join('.');
      return `${protocol}//${shellHost}/api/v1`;
    }
    return '/api/v1';
  }

  // AgenticRCM shell (dev CRA or production FE) — own /api → SaaS
  if (port === '3160' || port === '3000' || port === '3001' || port === '80' || port === '443' || !port) {
    if (isLocal && (port === '3000' || port === '3001')) {
      return `http://${hostname}:8001/api/v1`;
    }
    return '/api/v1';
  }

  // Embedded RCM service FE ports on localhost (:3150–:3161) — talk to local SaaS.
  return `http://${hostname}:8001/api/v1`;
}

/**
 * Fetch organizations.settings.rcm from SaaS (org/app SoT).
 * Returns { default_date_range, default_date_from, default_date_to, ... } or null.
 */
export async function fetchOrgRcmSettings() {
  if (typeof window === 'undefined') return null;

  // Prefer shell-passed settings (avoids CORS: iframe :315x → shell :3160 / SaaS).
  try {
    const q = new URLSearchParams(window.location.search);
    const encoded = (q.get('org_rcm') || '').trim();
    if (encoded) {
      try {
        const json = decodeURIComponent(escape(atob(encoded)));
        const parsed = JSON.parse(json);
        if (parsed && typeof parsed === 'object') return parsed;
      } catch {
        try {
          const parsed = JSON.parse(decodeURIComponent(encoded));
          if (parsed && typeof parsed === 'object') return parsed;
        } catch {
          /* fall through */
        }
      }
    }
  } catch {
    /* ignore */
  }
  try {
    const cached = sessionStorage.getItem('organization_rcm_settings');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch {
    /* ignore */
  }

  let queryOrgId = '';
  try {
    queryOrgId = new URLSearchParams(window.location.search).get('organization_id') || '';
  } catch {
    /* ignore */
  }
  const orgId =
    queryOrgId ||
    localStorage.getItem('organization_id') ||
    localStorage.getItem('selectedOrganizationId') ||
    localStorage.getItem('organizationId');

  // Prefer shell-passed ?auth_token= (iframe) so we do not use a stale JWT from a prior seed/login.
  let token = '';
  try {
    const q = new URLSearchParams(window.location.search);
    token = (q.get('auth_token') || q.get('token') || '').trim();
  } catch {
    /* ignore */
  }
  if (!token) {
    token =
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('access_token') ||
      '';
  }
  if (token) {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('auth_token', token);
    } catch {
      /* ignore quota / private mode */
    }
  }

  if (!orgId && !token) return null;

  const base = getSaasApiBaseUrlForOrgSettings();
  const headers = {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const urls = [];
  if (orgId) urls.push(`${base}/organizations/${orgId}`);
  urls.push(`${base}/organizations/current`);

  for (const url of urls) {
    try {
      const res = await fetch(url, { headers });
      if (!res.ok) continue;
      const data = await res.json();
      const rcm = data?.settings?.rcm;
      if (rcm && typeof rcm === 'object') return rcm;
      // Some responses nest organization
      const nested = data?.organization?.settings?.rcm;
      if (nested && typeof nested === 'object') return nested;
    } catch {
      /* try next */
    }
  }
  return null;
}
